/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectEEDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import SelectZone from "./SelectZone";
import CommonBreadcrumbs from "../../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import SelectComponentList from "./SelectComponentList";
import { labCar } from "../../../../redux/actions";
import { useSelector } from "react-redux";


import CustomCheckBoxWithoutFormControl from "../../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import CustomDialogBoxV2 from "../../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomSelectV2 from "../../../../components/customComponentsV2/common/select/CustomSelectV2";
import SelectDialogActionButtons from "./SelectDialogActionButtons";

const getSafeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

function SelectEEDialog({
  isZonalProject,
  zoneList = [],
  selectedZone,
  setSelectedZone,
  selectedZoneName,
  setSelectedZoneName,
  showZone,
  setShowZone,
  selectedEE,
  setSelectedEE,
  disabledEEComponents = [],
}) {
  const zone = "zone";
  const ee = "ee";

  const [eeComponentModifiedList, setEEComponentModifiedList] = useState([]);
  const [eeCount, setEECount] = useState(0);
  const [page, setPage] = useState(1);
  const rowsPerPage = 25;

  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);

  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [pendingEEComponent, setPendingEEComponent] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");

  const getSuppliersList = (component) => {
    if (!component) return [];
    const suppliers = component.supplier_name;
    if (Array.isArray(suppliers)) {
      return suppliers.map((name) => ({ label: name, value: name }));
    }
    if (typeof suppliers === "string") {
      return [{ label: suppliers, value: suppliers }];
    }
    return [];
  };

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      setProjectID(selectedProjectDetails?.project_id);
    }
    if (selectedSingleVariantDetails?.variant_id) {
      setVariantID(selectedSingleVariantDetails?.variant_id);
    }
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  const handleRemoveEEComponent = (eeId) => {
    setSelectedEE((prev) => getSafeArray(prev).filter((item) => item?.ee_id !== eeId));
  };

  const handleEECheckBoxChange = (e, data, isDisabled) => {
    e.stopPropagation();
    if (isDisabled) return;

    if (e.target.checked) {
      setPendingEEComponent(data);
      setSelectedSupplier("");
      setOpenSupplierDialog(true);
    } else {
      handleRemoveEEComponent(data?.ee_id);
    }
  };

  const handleAddPendingEEComponent = () => {
    if (pendingEEComponent && selectedSupplier) {
      const componentWithSupplier = {
        ...pendingEEComponent,
        selected_supplier: selectedSupplier,
      };
      setSelectedEE((prev) => {
        const safePrev = getSafeArray(prev);
        const exists = safePrev.some((item) => item?.ee_id === componentWithSupplier.ee_id);
        return exists ? safePrev : [...safePrev, componentWithSupplier];
      });
    }
    setOpenSupplierDialog(false);
    setPendingEEComponent(null);
    setSelectedSupplier("");
  };

  const modifyEEData = (data) => {
    const modifiedList = data.map((item) => {
      return {
        data: item,
        id: item.ee_id,
        cardDataArray: [
          {
            key: "Number",
            value: item.assembly_number,
            keyStyle: { fontWeight: 600 },
            valueStyle: { fontWeight: 600 },
          },
          {
            key: "Description",
            value: item.description,
          },
          {
            key: "Version",
            value: item.version,
          },
        ],
      };
    });
    setEEComponentModifiedList(modifiedList);
  };

  const getChangedZone = (zone, zoneName) => {
    setSelectedZone(zone);
    setSelectedZoneName(zoneName);
    setShowZone(ee);
  };

  return (
    <Box>
      {showZone === zone && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              items={[
                {
                  label: "Select Zone",
                },
              ]}
            />
          </Box>
          <SelectZone
            zoneList={zoneList}
            getChangedZone={getChangedZone}
            selectedZone={selectedZone}
          />
        </Box>
      )}
      {showZone === ee && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              color={"black"}
              items={[
                ...(isZonalProject
                  ? [
                      {
                        label: selectedZoneName,
                        onClick: () => {
                          setShowZone(zone);
                        },
                      },
                    ]
                  : []),
                {
                  label: "EE Component",
                },
              ]}
            />
          </Box>
          <Box>
            <Box>
              <SelectComponentList
                dataList={eeComponentModifiedList}
                apiAction={labCar.get_ee_components_new}
                searchKey={"assembly_number"}
                placeholder={"Search EE"}
                page={page}
                rowsPerPage={rowsPerPage}
                handlePageChange={(page) => setPage(page)}
                totalCount={eeCount}
                enableApiRequest={projectID && variantID}
                isItemDisabled={(item) =>
                  disabledEEComponents.includes(Number(item?.data?.ee_id))
                }
                renderSelectIcon={(data) => {
                  const isDisabled = disabledEEComponents.includes(
                    Number(data.ee_id),
                  );
                  const isChecked = Array.isArray(selectedEE)
                    ? selectedEE.some((item) => item?.ee_id === data?.ee_id)
                    : selectedEE?.ee_id === data?.ee_id;
                  return (
                    <Box
                      sx={{ pl: 1.5, pt: 2 }}
                    >
                      <CustomCheckBoxWithoutFormControl
                        value={isChecked}
                        disabled={isDisabled}
                        onChange={(e) => handleEECheckBoxChange(e, data, isDisabled)}
                      />
                    </Box>
                  );
                }}
                renderCardActionIcons={(data) => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pt: 2,
                        pr: 2,
                      }}
                    >
                    </Box>
                  );
                }}
                getDataOnSearch={(data, resp) => {
                  if (resp.error) {
                    modifyEEData([]);
                  } else {
                    modifyEEData(resp.data);
                    setEECount(resp.total_count);
                  }
                }}
                params={{
                  ...(projectID && variantID
                    ? {
                        project_id: Number(projectID),
                        variant_id: Number(variantID),
                        ...(isZonalProject && { zone_position: selectedZone }),
                        status: "true",
                        page_number: page,
                        page_size: rowsPerPage,
                      }
                    : {}),
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
      <CustomDialogBoxV2
        title="Select Supplier"
        open={openSupplierDialog}
        onClose={() => {
          setOpenSupplierDialog(false);
          setPendingEEComponent(null);
          setSelectedSupplier("");
        }}
        dialogActions={
          <SelectDialogActionButtons
            enableSubmit={!!selectedSupplier}
            onClickAdd={handleAddPendingEEComponent}
            onClickCancel={() => {
              setOpenSupplierDialog(false);
              setPendingEEComponent(null);
              setSelectedSupplier("");
            }}
          />
        }
      >
        <Box sx={{ p: 2, minWidth: "300px" }}>
        
          <CustomSelectV2
            label="Select Supplier"
            optionsList={getSuppliersList(pendingEEComponent)}
            selectedValue={selectedSupplier}
            handleOnChange={(e) => setSelectedSupplier(e.target.value)}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

SelectEEDialog.propTypes = {
  isZonalProject: PropTypes.bool,
  zoneList: PropTypes.array,
  selectedZone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedZone: PropTypes.func,
  selectedZoneName: PropTypes.string,
  setSelectedZoneName: PropTypes.func,
  showZone: PropTypes.string,
  setShowZone: PropTypes.func,
  selectedEE: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),
  setSelectedEE: PropTypes.func,
  disabledEEComponents: PropTypes.array,
};

export default SelectEEDialog;
